import React, { useEffect, useState } from 'react';
import { Droplet, X } from 'lucide-react';
import { useHabitDisciplinePrompt } from '../../stores/habitDisciplinePromptStore';
import { useHydrationStore } from '../../stores/hydrationStore';
import { DRINK_PRESETS_ML } from '../../utils/hydration';

// Global host: when a hydration-linked habit is completed, the habit store parks
// a request; this surfaces the "how much did you drink?" prompt and logs it to
// the water tracker. Mounted once at the app root.
export function HydrationLogPromptHost() {
  const pending = useHabitDisciplinePrompt((s) => s.hydration);
  const clear = useHabitDisciplinePrompt((s) => s.clear);
  const logDrink = useHydrationStore((s) => s.logDrink);
  const initHydration = useHydrationStore((s) => s.initialize);
  const [custom, setCustom] = useState('');

  // Load today's tracker before logging so we add to (not overwrite) real intake.
  useEffect(() => {
    if (pending) void initHydration();
  }, [pending, initHydration]);

  if (!pending) return null;

  const close = () => {
    setCustom('');
    clear();
  };

  const log = (ml: number) => {
    if (ml > 0) void initHydration().then(() => logDrink(ml));
    close();
  };

  const submitCustom = () => {
    const n = Math.round(Number(custom));
    if (Number.isFinite(n) && n > 0) log(n);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4">
      <div className="bg-card border border-border rounded-xl shadow-lg w-full max-w-sm p-6">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3b82f620' }}>
              <Droplet className="w-5 h-5" style={{ color: '#3b82f6' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Log your water</h3>
              <p className="text-xs text-muted-foreground">Completed “{pending.habitName}” — how much did you drink?</p>
            </div>
          </div>
          <button onClick={close} className="p-1.5 text-muted-foreground hover:text-foreground rounded-md transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          {DRINK_PRESETS_ML.map((ml) => (
            <button
              key={ml}
              onClick={() => log(ml)}
              className="px-3 py-3 rounded-lg border border-border hover:border-primary/60 hover:bg-muted/50 text-sm font-medium text-foreground transition-colors"
            >
              {ml} ml
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-3">
          <input
            type="number"
            inputMode="numeric"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitCustom(); }}
            placeholder="Custom ml"
            className="flex-1 px-3 py-2 bg-transparent border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
          />
          <button
            onClick={submitCustom}
            disabled={!custom.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors text-sm"
          >
            Log
          </button>
        </div>

        <button onClick={close} className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
          Skip
        </button>
      </div>
    </div>
  );
}
